import CustomHTMLElement from '@onpace/onspace-core/components/html_element'

/// An element which handles the composition of form items.
///
/// this works by allowing items to be added, removed and rearranged on the page. When adding a new element, a new
/// FormItemControl is created, with it's contents coming from the template. When removing an element, the control is
/// removed from the dom. Finally when rearranging, the items are reordered, with their hidden +order+ field being
// updated.
export default class FormItem extends CustomHTMLElement {
  /// Setsup up the form item element.
  ///
  /// This locates the required children and adds events where necessary.
  runConstructor() {
    super.runConstructor()

    this.name = this.getAttribute('name')
    this.array = this.getBooleanAttribute('multiple')

    this.controlTemplates = {}
    this.querySelectorAll('[data-item-template]').forEach((template) => {
      const key = template.getAttribute('data-item-template')
      template.remove()
      this.controlTemplates[key] = template
    })

    this.addDropDown = this.querySelector('drop-down[data-add-items]')
    this.querySelectorAll('[data-add-item]').forEach((element) => {
      const key = element.getAttribute('data-add-item')
      element.addEventListener('click', () => {
        if (this.addDropDown) { this.addDropDown.hideMenu() }
        this.addControl(key)
      })
    })

    this.addEventListener('dragover', this.draggedOver.bind(this))
  }

  /// Detects whether this element is disabled, based on a parent fieldset.
  get disabled() {
    const fieldset = this.closest('fieldset')
    if (fieldset) {
      return fieldset.disabled
    } else {
      return false
    }
  }

  ////////// Items

  /// Retrieves the control elements from the DOM.
  get controlElements() {
    return this.querySelectorAll('form-item-control')
  }

  /// Creates and adds a new control element from the given +key+.
  ///
  /// If this is not in multiple mode, any existing controls will be removed.
  addControl(key) {
    if (this.disabled) { return }
    if (!this.array) { this.controlElements.forEach((c) => c.remove()) }

    const template = this.controlTemplates[key]
    if (!template) { return }

    const control = new FormItemControl(template)
    this.insertBefore(control, this.addDropDown)

    this.updateOrderValues()
    this.triggerEvent('form-item:add-control', control)
  }

  ////////// Ordering

  /// Updates the value of each control's hidden +order+ input.
  ///
  /// This updates all of the order values to match their order in the DOM. It is called automatically whenever items
  /// are added, removed and rearranged.
  updateOrderValues() {
    this.controlElements.forEach((element, i) => {
      element.orderInput.value = i
    })
  }

  /// Initiates a drag.
  ///
  /// When a control begins a drag, it calls this function to notify the element and setup for the drag.
  beginDrag(control) {
    this.draggingControl = control
    this.dragControls = Array.from(this.controlElements)
    this.dragIndex = this.dragControls.indexOf(this.draggingControl)

    const offset = document.documentElement.scrollTop - document.documentElement.clientTop
    this.dragCenters = this.dragControls.map((i) => {
      const rect = i.getBoundingClientRect()
      return rect.top + (rect.height / 2) + offset
    })
  }

  /// Finishes a drag.
  ///
  /// When a control ends a drag, it calls this function to notify this element and cleanup from the drag.
  endDrag() {
    this.draggingControl = null
    this.dragControls = null
    this.dragIndex = null
    this.dragCenters = null

    this.updateOrderValues()
  }

  /// Updates from a current drag.
  ///
  /// This callback is called during a drag, when the control is above this element. The position of the dragged control
  /// relative to the other controls is calculated, and its position in the DOM is changed if necessary.
  draggedOver(event) {
    if (!this.draggingControl) { return }
    event.preventDefault()

    let i = -1
    do { i += 1 } while (i < this.dragControls.length && event.pageY > this.dragCenters[i])

    const insertIndex = i
    if (i > this.dragIndex) { i -= 1 }

    if (this.dragIndex != i) {
      this.dragIndex = i
      this.insertBefore(this.draggingControl, this.children[insertIndex])
      this.triggerEvent('form-item:order')
    }
  }
}

/// An element which operates a single control within a form item.
///
/// This is responsible for the item-specific functionality of it's parent element.
export class FormItemControl extends CustomHTMLElement {
  /// Sets up the control element.
  runConstructor(template) {
    super.runConstructor()

    if (template) {
      const index = Math.floor(Math.random() * 1000)
      this.innerHTML = template.innerHTML.replace(/\[\$idx\]/g, `[*${index}]`)
      this.innerHTML = template.innerHTML.replace(/_\$idx/g, index.toString())
    }

    this.orderInput = this.querySelector('[data-form-item-order]')

    this.dragButton = this.querySelector('[data-form-item-drag]')
    if (this.dragButton) {
      this.draggable = true
      this.addEventListener('dragstart', this.dragStarted.bind(this))
      this.addEventListener('dragend', this.dragEnded.bind(this))
    }

    this.removeButton = this.querySelector('[data-form-item-remove]')
    if (this.removeButton) {
      this.removeButton.addEventListener('click', this.removeControl.bind(this))
    }
  }

  ////////// Item

  /// Retrieves the parent FormItem element.
  get formItem() {
    return this.parentElement
  }

  /// Removes this control from the DOM.
  removeControl(_event) {
    if (this.disabled) { return }

    const formItem = this.formItem

    this.triggerEvent('form-item:remove-control', formItem)
    this.remove()

    formItem.updateOrderValues()
  }

  ////////// Ordering

  /// Initiates a drag.
  ///
  /// This is called when a user picks up the drag button. It notified the parent FormItem and prepares itself for a
  /// drag.
  dragStarted(event) {
    if (this.disabled) { return event.preventDefault() }

    const eventElement = document.elementFromPoint(event.clientX, event.clientY)
    if (!eventElement || !eventElement.closest('[data-form-item-drag]')) { return event.preventDefault() }

    this.formItem.beginDrag(this)

    setTimeout(() => this.style.opacity = 0)
  }

  /// Finishes a drag.
  ///
  /// This is called when a user drops the dragged element. It notifies the parent FormControl and cleans itself up from
  /// the drag.
  dragEnded(_event) {
    if (this.disabled) { return }

    this.formItem.endDrag()

    this.style.opacity = ''
  }
}

window.customElements.define('form-item', FormItem)
window.customElements.define('form-item-control', FormItemControl)
